import { Box, List } from '@factorialco/deprecated-design-system'
import isNil from 'lodash/isNil'
import orderBy from 'lodash/orderBy'
import moment from 'moment'

import { observer } from 'mobx-react'

import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { EmptyState } from 'modules/notifications/components/EmptyState'
import type { AvatarUser, InboxRequest } from 'modules/notifications/hooks/useNotificationsRequests'

import { RequestItem } from './RequestItem'

function sortRequests(requests: InboxRequest[]) {
  return orderBy(
    requests,
    [
      (request) => isNil(request.dueDate), // First, things with due date
      (request) => request.dueDate && moment(request.dueDate).valueOf(), // Then, sort by due date
      (request) => stringToDate(request.resourceCreatedAt, 'backend_datetime').toDate(), // Then, sort by created_at
      (request) => request.text, // Then, sort by text
    ],
    ['asc', 'asc', 'desc', 'asc']
  )
}

interface Props {
  requests: InboxRequest[]
  // Remove the request with the passed 'id' from the list if
  // the passed 'id' is found
  removeRequestById: (id: string) => void
  compact?: boolean
  avatarUsersMap?: Map<string, AvatarUser>
}

export const RequestItems = observer(
  ({ compact, requests, removeRequestById, avatarUsersMap }: Props) => {
    const isEmpty = !requests.length

    const listItems = sortRequests(requests).map((request, index) => {
      const last = index == requests.length - 1

      return (
        <Box
          width="full"
          key={request.id}
          aria-label="Inbox requests"
          {...(!last && { border: { bottom: { width: 's1', style: 'solid', color: 'grey400' } } })}
        >
          <RequestItem
            key={request.id}
            index={index}
            request={request}
            compact={compact}
            removeRequestById={removeRequestById}
            avatarUsersMap={avatarUsersMap}
          />
        </Box>
      )
    })

    return (
      <>
        {compact ? (
          isEmpty ? (
            <Box alignItems="center" justifyContent="center" height="full">
              <EmptyState
                title={i18n.t('dashboard.tasks.empty.first')}
                subtitle={i18n.t('dashboard.tasks.empty.second')}
                compact
              />
            </Box>
          ) : (
            <Box width="full">{listItems}</Box>
          )
        ) : isEmpty ? (
          <Box width="full">
            <Box justifyContent="center">
              <EmptyState
                title={i18n.t('dashboard.tasks.empty.first')}
                subtitle={i18n.t('dashboard.tasks.empty.second')}
              />
            </Box>
          </Box>
        ) : (
          <Box width="full">
            <List>{listItems}</List>
          </Box>
        )}
      </>
    )
  }
)
